import { useEffect, useState } from 'react';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { useApp } from '@/components/AppLayout';
import {
  reportsApi,
  type OutstandingInvoicesReport,
  type OutstandingInvoiceRow,
  type ReportExportFormat,
} from '@/lib/api';
import { formatAmount } from '@/lib/currency';
import { DataTable, SortableHeader } from '@/components/ui/data-table';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  ReportLayout,
  SummaryCard,
  SummaryCardsGrid,
  ExportDropdown,
} from '@/components/reports';
import type { ColumnDef } from '@tanstack/react-table';
import { CheckCircle2 } from 'lucide-react';

export default function OutstandingInvoices() {
  const { nav } = useBusinessNav();
  const { currentBusiness } = useApp();
  const [report, setReport] = useState<OutstandingInvoicesReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [groupByClient, setGroupByClient] = useState(false);

  useEffect(() => {
    if (currentBusiness) {
      // Clear report when groupByClient changes to avoid data structure mismatch
      setReport(null);
      loadReport();
    }
  }, [currentBusiness, groupByClient]);

  const loadReport = async () => {
    if (!currentBusiness) return;
    setLoading(true);
    try {
      const response = await reportsApi.getOutstandingInvoices(currentBusiness.id, groupByClient);
      if (response.data) {
        setReport(response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: ReportExportFormat) => {
    if (!currentBusiness) return;
    await reportsApi.exportOutstandingInvoices(currentBusiness.id, format, groupByClient);
  };

  const columns: ColumnDef<OutstandingInvoiceRow>[] = [
    {
      accessorKey: 'invoice_number',
      header: ({ column }) => <SortableHeader column={column} title="Invoice #" />,
      cell: ({ row }) => (
        <span className="font-medium">{row.getValue('invoice_number')}</span>
      ),
    },
    {
      accessorKey: 'client_name',
      header: ({ column }) => <SortableHeader column={column} title="Client" />,
    },
    {
      accessorKey: 'due_date',
      header: ({ column }) => <SortableHeader column={column} title="Due Date" />,
      cell: ({ row }) => {
        const date = row.getValue('due_date') as string | null;
        return date || '-';
      },
    },
    {
      accessorKey: 'days_overdue',
      header: ({ column }) => <SortableHeader column={column} title="Days Overdue" />,
      cell: ({ row }) => {
        const days = row.getValue('days_overdue') as number;
        if (days <= 0) {
          return <span className="text-green-600">Current</span>;
        }
        return <span className="text-red-600">{days} days</span>;
      },
    },
    {
      accessorKey: 'amount_due',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Amount Due" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right font-medium">
          {formatAmount(row.getValue('amount_due'))}
        </div>
      ),
    },
  ];

  // Flatten grouped data for table display
  const getTableData = (): OutstandingInvoiceRow[] => {
    if (!report || !report.data) return [];

    if (groupByClient) {
      // Grouped data - flatten for display
      const grouped = report.data as Array<{
        client_name: string;
        total_outstanding: string;
        invoices: OutstandingInvoiceRow[];
      }>;
      // Safely flatten - check if each group has invoices array
      return grouped.flatMap((group) => group.invoices || []);
    }

    return report.data as OutstandingInvoiceRow[];
  };

  return (
    <ReportLayout
      title="Outstanding Invoices"
      actions={<ExportDropdown onExport={handleExport} disabled={loading || !report} />}
      filters={
        <div className="flex items-center space-x-2">
          <Checkbox
            id="group-by-client"
            checked={groupByClient}
            onCheckedChange={(checked) => setGroupByClient(checked === true)}
          />
          <Label htmlFor="group-by-client" className="cursor-pointer">
            Group by client
          </Label>
        </div>
      }
    >
      {report && (
        <div className="mb-6">
          <SummaryCardsGrid>
            <SummaryCard
              label="Total Outstanding"
              value={formatAmount(report.summary.total_outstanding)}
              subLabel={`${report.summary.invoice_count} invoices`}
              variant="default"
            />
            <SummaryCard
              label="Current"
              value={formatAmount(report.summary.buckets.current.amount)}
              subLabel={`${report.summary.buckets.current.count} invoices`}
              variant="success"
            />
            <SummaryCard
              label="1-30 Days"
              value={formatAmount(report.summary.buckets['1_30'].amount)}
              subLabel={`${report.summary.buckets['1_30'].count} invoices`}
            />
            <SummaryCard
              label="31-60 Days"
              value={formatAmount(report.summary.buckets['31_60'].amount)}
              subLabel={`${report.summary.buckets['31_60'].count} invoices`}
              variant="warning"
            />
            <SummaryCard
              label="61-90 Days"
              value={formatAmount(report.summary.buckets['61_90'].amount)}
              subLabel={`${report.summary.buckets['61_90'].count} invoices`}
              variant="danger"
            />
            <SummaryCard
              label="90+ Days"
              value={formatAmount(report.summary.buckets['90_plus'].amount)}
              subLabel={`${report.summary.buckets['90_plus'].count} invoices`}
              variant="danger"
            />
          </SummaryCardsGrid>
        </div>
      )}
      <DataTable
        columns={columns}
        data={report ? getTableData() : []}
        loading={loading}
        onRowClick={(row) => nav(`/invoices/${row.invoice_id}`)}
        emptyIcon={<CheckCircle2 className="h-6 w-6 text-green-600" />}
        emptyTitle="No Outstanding Invoices"
        emptyDescription="All your invoices have been paid."
      />
    </ReportLayout>
  );
}
